import { useRef, useState, useEffect, useCallback } from "react";
import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import useWhiteLabelConfig from "src/contexts/config-context";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import useHousehold from "src/contexts/household-context";
import { useRouter } from "src/hooks/use-router";
import { useAuth } from "src/hooks/use-auth";
import { Issuer } from "src/utils/auth";

import { RouterLink } from "src/components/router-link";
import { paths } from "src/paths";

const TOP_NAV_HEIGHT: number = 80;

const LayoutRoot = styled("div")(({ theme }) => ({
  backgroundColor: "#FAF9F8",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "top center",
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  height: "100%",
}));

interface LayoutProps {
  children: ReactNode;
  onBack?: () => void | Promise<void>;
  hideHeader?: boolean;
  containerMaxWidth?: "sm" | "md" | "lg" | "xl";
}

export const Layout: FC<LayoutProps> = (props) => {
  const { children, onBack, hideHeader = false, containerMaxWidth = "sm" } = props;
  const { professional_group } = useWhiteLabelConfig();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const router = useRouter();
  const auth = useAuth();

  const { selectedHousehold } = useHousehold();

  const headOfHousehold = selectedHousehold?.head_of_household;

  // Handler for back to home button
  const handleBackToHome = async () => {
    if (onBack) {
      // Use custom handler if provided
      await onBack();
    } else {
      // Default handler
      try {
        const totalViewPreference = sessionStorage.getItem(
          "totalViewPreference"
        );

        router.push(
          totalViewPreference === "express" ? "/express" : paths.index
        );
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      switch (auth.issuer) {
        case Issuer.JWT: {
          await auth.signOut();
          break;
        }

        default: {
          console.warn("Using an unknown Auth Issuer, did not log out");
        }
      }

      router.push(
        paths.auth.jwt.entryStart + "?returnTo=" + window.location.pathname
      );
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  }, [auth, router]);

  useEffect(() => {
    const handleResize = () => {
      if (
        imageRef.current &&
        professional_group?.app_logo &&
        professional_group?.total_app_logo_multiplier
      ) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        const aspectRatio = naturalWidth / naturalHeight;

        // Check if mobile screen
        const isMobile = window.innerWidth < 600; // MUI 'sm' breakpoint
        const mobileMultiplier = 0.7; // 70% of desktop size on mobile

        let maxWidth = 180;
        let maxHeight = 30;

        // Adjust maxHeight for aspect ratios
        if (aspectRatio < 2.9) {
          maxHeight = 48;
        }
        if (aspectRatio >= 2.9 && aspectRatio <= 4) {
          maxHeight = 40;
        }
        if (aspectRatio > 4) {
          maxHeight = 35;
        }

        // Apply mobile reduction
        if (isMobile) {
          maxWidth = maxWidth * mobileMultiplier;
          maxHeight = maxHeight * mobileMultiplier;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          if (maxWidth / aspectRatio < maxHeight) {
            setDimensions({
              width: maxWidth * professional_group?.total_app_logo_multiplier,
              height:
                (maxWidth / aspectRatio) *
                professional_group?.total_app_logo_multiplier,
            });
          } else {
            setDimensions({
              width:
                maxHeight *
                aspectRatio *
                professional_group?.total_app_logo_multiplier,
              height: maxHeight * professional_group?.total_app_logo_multiplier,
            });
          }
        } else {
          setDimensions({
            width: naturalWidth * professional_group?.total_app_logo_multiplier,
            height:
              naturalHeight * professional_group?.total_app_logo_multiplier,
          });
        }
      }
    };

    const img = imageRef.current;

    if (img && professional_group?.app_logo) {
      img.onload = handleResize;
      img.src = professional_group?.app_logo;
    }

    // Add resize listener to handle screen size changes
    window.addEventListener("resize", handleResize);

    return () => {
      if (img) {
        img.onload = null;
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [
    professional_group?.app_logo,
    professional_group?.total_app_logo_multiplier,
  ]);

  return (
    <LayoutRoot>
      {!hideHeader && (
      <Box
        component="header"
        sx={{
          left: 0,
          position: "fixed",
          right: 0,
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: "#FAF9F8",
          borderBottom: "1px solid #e7e7e7",
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} sx={{ height: TOP_NAV_HEIGHT }}>
            <Stack
              alignItems={"center"}
              direction="row"
              spacing={1}
              sx={{ width: "100%" }}
              justifyContent={"space-between"}
            >
              <Box
                component={RouterLink}
                href={paths.index}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  maxHeight: TOP_NAV_HEIGHT - 16,
                }}
              >
                {professional_group?.app_logo ? (
                  <img
                    ref={imageRef}
                    src={professional_group.app_logo}
                    style={{
                      maxWidth: dimensions.width,
                      maxHeight: TOP_NAV_HEIGHT - 16,
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                    alt="Logo"
                  />
                ) : (
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: "24px",
                      color: "primary.main",
                    }}
                  >
                    Snug
                  </Box>
                )}
              </Box>
              {headOfHousehold?.estate_plan_foundation ? (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
                  onClick={handleBackToHome}
                >
                  Back
                </Button>
              ) : (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  onClick={handleLogout}
                >
                  Log out
                </Button>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>
      )}
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          flex: "1 1 auto",
        }}
      >
        <Container
          maxWidth={containerMaxWidth}
          sx={{
            py: {
              xs: "100px",
              md: "120px",
            },
          }}
        >
          {children}
        </Container>
      </Box>
    </LayoutRoot>
  );
};

Layout.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
};
